import type { BoxProps } from '@hover/blueprint';
import { Body, Box, motion } from '@hover/blueprint';
import React from 'react';

import { Carousel } from 'src/modules/renderings/components/RenderingsHub/Carousel';
import { FeaturedProducts } from 'src/modules/renderings/components/RenderingsHub/FeaturedProduct/FeaturedProducts';
import { HeaderControls } from 'src/modules/renderings/components/RenderingsHub/HeaderControls';
import { InteractiveRenderingsLink } from 'src/modules/renderings/components/RenderingsHub/InteractiveRenderingsLink/InteractiveRenderingsLink';
import { useIsDesktopSize } from 'src/modules/renderings/hooks';
import { useFocusedView } from 'src/modules/renderings/hooks/useFocusedView';
import { useHeaderHeight } from 'src/modules/renderings/hooks/useHeaderHeight';
import type { ImageLike } from 'src/modules/renderings/imageLike';

import { BackButton } from '../BackButton';
import { DownloadImage } from '../DownloadImage';
import { OrgBranding } from '../OrgBranding';
import { OnlyShowInLandscape } from '../responsiveHelpers';

/*
 The margin on this container prevents a tiny horizontal scroll when the InteractiveRenderingsLink
 is visible in a tablet sized window. Don't remove this margin unless you've tested this edge case.
 The core issue is likely the negative margins on chakra Link component
 */
const ImageControlsContainer = (props: BoxProps) => (
  <Box {...props} marginRight={{ tablet: 400, desktop: 'unset' }} />
);

const RenderingView = ({
  heroImgIndex,
  images,
  name,
  address,
  onCarouselSwipe,
  hideBackButton = false,
  shareButton,
}: {
  heroImgIndex: number;
  images: ImageLike[];
  name?: string;
  address?: string;
  onCarouselSwipe: (idx: number) => void;
  hideBackButton?: boolean;
  shareButton: JSX.Element;
}) => {
  const heroImg = images[heroImgIndex];
  const heroImgUrl = heroImg?.blobUrl;
  const headerHeight = useHeaderHeight();
  const { isFocusedView } = useFocusedView();
  const isDesktopSize = useIsDesktopSize();

  const displayLocalHeader = isFocusedView ? 'none' : 'flex';
  const displayLocalFooter = isFocusedView ? 'none' : 'flex';
  const justifyContent = isFocusedView ? 'center' : 'none';
  const maxCarouselHeight = isFocusedView
    ? `calc(100vh - ${headerHeight}px)`
    : undefined;

  const maxCarouselContainerWidth = isFocusedView
    ? { tablet: 'calc(150vh)', mobile: undefined }
    : { desktop: 'calc(115vh)', mobile: undefined };

  return (
    <Box
      flexDirection="column"
      justifyContent={justifyContent}
      paddingBottom={{ base: 0, tablet: 300 }}
      paddingLeft={{ base: 0, desktop: 600 }}
      paddingRight={{ base: 0, desktop: 400 }}
      paddingTop={{ base: isFocusedView ? 0 : 300, tablet: 400 }}
      width="100%"
    >
      <Box
        as={motion.div}
        display={displayLocalHeader}
        height={500}
        justifyContent="space-between"
        layout
        width="100%"
      >
        <Box alignItems="center">
          {!hideBackButton && <BackButton />}
          <OnlyShowInLandscape>
            <Box
              alignItems="flex-start"
              flexDirection="column"
              marginLeft={400}
            >
              {name && (
                <Body color="neutral550" marginBottom={0}>
                  {name}
                </Body>
              )}
              {address && (
                <Body
                  fontWeight={700}
                  isBold
                  lineHeight={400}
                  marginTop={0}
                  size={600}
                >
                  {address}
                </Body>
              )}
            </Box>
          </OnlyShowInLandscape>
        </Box>
        <ImageControlsContainer>
          <DownloadImage
            color="neutral.900"
            imageUrl={heroImgUrl}
            translationKey="renderings.download"
          />
          {shareButton}
          <InteractiveRenderingsLink />
        </ImageControlsContainer>
      </Box>
      <Box
        display="block"
        margin={{ tablet: '0 auto', mobile: undefined }}
        maxWidth={maxCarouselContainerWidth}
        position="relative"
        width="100%"
      >
        <Carousel
          images={images}
          maxHeight={maxCarouselHeight}
          onSwipe={onCarouselSwipe}
          selectedIndex={heroImgIndex}
          testId="renderings-hero-image-portrait"
        />
        {!isFocusedView && <FeaturedProducts img={heroImg} />}
      </Box>
      {isDesktopSize && (
        <Box
          as={motion.div}
          display={displayLocalFooter}
          justifyContent="space-between"
          layout
          marginTop={{ base: 0, tablet: 300 }}
        >
          <OrgBranding />
        </Box>
      )}
      <HeaderControls imageUrl={heroImgUrl} shareButton={shareButton} />
    </Box>
  );
};

export { RenderingView };
